/**
 * Document Library Page - ISOCNTRL
 * Categorized document library with accordion-style categories,
 * search, upload, and download functionality
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Search, Upload, ChevronDown, ChevronUp, Download, Trash2,
  FileText, FileCheck, Megaphone, Wrench, ClipboardList,
  File, Plus, FolderOpen
} from 'lucide-react';
import { toast } from 'sonner';
import useAuthStore from '../lib/auth-store';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const ICON_MAP = {
  'file-check': FileCheck,
  'megaphone': Megaphone,
  'wrench': Wrench,
  'clipboard-list': ClipboardList,
  'file-text': FileText,
  'folder': FolderOpen,
};

function CategoryIcon({ iconName, className = "h-5 w-5" }) {
  const Icon = ICON_MAP[iconName] || FileText;
  return <Icon className={className} />;
}

function FileTypeIcon({ fileType }) {
  const colorMap = {
    PDF: 'bg-red-100 text-red-600',
    DOCX: 'bg-blue-100 text-blue-600',
    DOC: 'bg-blue-100 text-blue-600',
    XLSX: 'bg-green-100 text-green-600',
    XLS: 'bg-green-100 text-green-600',
    PPTX: 'bg-orange-100 text-orange-600',
    PPT: 'bg-orange-100 text-orange-600',
    MP4: 'bg-purple-100 text-purple-600',
  };
  const classes = colorMap[fileType] || 'bg-gray-100 text-gray-600';
  return (
    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${classes}`}>
      <File className="h-5 w-5" />
    </div>
  );
}

export default function DocumentsPage() {
  const { user } = useAuthStore();
  const [categories, setCategories] = useState([]);
  const [documents, setDocuments] = useState({});
  const [expandedCategories, setExpandedCategories] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadTitle, setUploadTitle] = useState('');
  const [uploadDescription, setUploadDescription] = useState('');
  const [uploadCategoryId, setUploadCategoryId] = useState('');
  const [uploading, setUploading] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [downloadingId, setDownloadingId] = useState(null);

  const isAdmin = user?.role === 'super_admin' || user?.role === 'iso';

  const getHeaders = useCallback(() => {
    const token = localStorage.getItem('token');
    return { Authorization: `Bearer ${token}` };
  }, []);

  const loadCategories = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/api/library/categories`, { headers: getHeaders() });
      if (!res.ok) throw new Error('Failed to load');
      const data = await res.json();
      setCategories(data);
    } catch (err) {
      console.error('Failed to load categories:', err);
    }
  }, [getHeaders]);

  const loadDocumentsForCategory = useCallback(async (categoryId) => {
    try {
      const res = await fetch(
        `${API_BASE}/api/library/documents?category_id=${categoryId}`,
        { headers: getHeaders() }
      );
      if (!res.ok) throw new Error('Failed to load');
      const data = await res.json();
      setDocuments(prev => ({ ...prev, [categoryId]: data }));
    } catch (err) {
      console.error('Failed to load documents:', err);
    }
  }, [getHeaders]);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await loadCategories();
      setLoading(false);
    };
    init();
  }, [loadCategories]);

  const toggleCategory = async (categoryId) => {
    const isExpanding = !expandedCategories[categoryId];
    setExpandedCategories(prev => ({ ...prev, [categoryId]: isExpanding }));
    if (isExpanding && !documents[categoryId]) {
      await loadDocumentsForCategory(categoryId);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      setSearchResults(null);
      return;
    }
    try {
      const res = await fetch(
        `${API_BASE}/api/library/documents?search=${encodeURIComponent(searchQuery)}`,
        { headers: getHeaders() }
      );
      if (!res.ok) throw new Error('Search failed');
      const data = await res.json();
      setSearchResults(data);
    } catch (err) {
      console.error('Search failed:', err);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => { handleSearch(); }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const handleUpload = async () => {
    if (!uploadFile || !uploadTitle || !uploadCategoryId) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', uploadFile);
      formData.append('category_id', uploadCategoryId);
      formData.append('title', uploadTitle);
      formData.append('description', uploadDescription);

      const res = await fetch(`${API_BASE}/api/library/documents/upload`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        body: formData,
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Upload failed');
      }

      toast.success('Document uploaded successfully');
      setUploadDialogOpen(false);
      setUploadFile(null);
      setUploadTitle('');
      setUploadDescription('');
      setUploadCategoryId('');
      await loadCategories();
      if (documents[uploadCategoryId]) {
        await loadDocumentsForCategory(uploadCategoryId);
      }
    } catch (err) {
      toast.error(err.message);
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (doc) => {
    setDownloadingId(doc.id);
    try {
      const res = await fetch(
        `${API_BASE}/api/library/documents/${doc.id}/download`,
        { headers: getHeaders() }
      );
      if (!res.ok) throw new Error('Download failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.file_name;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('Failed to download document');
    } finally {
      setDownloadingId(null);
    }
  };

  const handleDelete = async (docId, categoryId) => {
    try {
      const res = await fetch(`${API_BASE}/api/library/documents/${docId}`, {
        method: 'DELETE',
        headers: getHeaders(),
      });
      if (!res.ok) throw new Error('Delete failed');
      toast.success('Document deleted');
      await loadCategories();
      await loadDocumentsForCategory(categoryId);
    } catch (err) {
      toast.error('Failed to delete document');
    }
  };

  const handleCreateCategory = async () => {
    if (!newCategoryName.trim()) return;
    try {
      const res = await fetch(`${API_BASE}/api/library/categories`, {
        method: 'POST',
        headers: { ...getHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newCategoryName }),
      });
      if (!res.ok) throw new Error('Failed to create');
      toast.success('Category created');
      setCategoryDialogOpen(false);
      setNewCategoryName('');
      await loadCategories();
    } catch (err) {
      toast.error(err.message);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  const displayDocs = searchResults !== null ? searchResults : null;

  return (
    <div data-testid="documents-page">
      {/* Header */}
      <div className="flex items-start justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold">Document Library</h1>
          <p className="text-muted-foreground mt-1">
            Organized document categories for CRM and boarding process
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              type="search"
              placeholder="Search documents..."
              className="pl-9 w-64"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              data-testid="document-search-input"
            />
          </div>
          {isAdmin && (
            <Button
              onClick={() => setUploadDialogOpen(true)}
              className="bg-[#3b82f6] hover:bg-[#2563eb]"
              data-testid="upload-document-btn"
            >
              <Upload className="h-4 w-4 mr-2" />
              Upload Document
            </Button>
          )}
        </div>
      </div>

      {/* Search Results */}
      {displayDocs !== null && (
        <Card className="mb-6 p-6" data-testid="search-results">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold">
              Search Results ({displayDocs.length})
            </h2>
            <Button variant="ghost" size="sm" onClick={() => { setSearchQuery(''); setSearchResults(null); }}>
              Clear Search
            </Button>
          </div>
          {displayDocs.length === 0 ? (
            <p className="text-muted-foreground text-center py-8">No documents found</p>
          ) : (
            <div className="space-y-2">
              {displayDocs.map(doc => (
                <DocumentRow key={doc.id} doc={doc} onDownload={handleDownload} onDelete={isAdmin ? handleDelete : null} downloadingId={downloadingId} />
              ))}
            </div>
          )}
        </Card>
      )}

      {/* Categories Accordion */}
      {displayDocs === null && (
        <div className="space-y-3" data-testid="categories-list">
          {categories.map((cat) => (
            <Card key={cat.id} className="overflow-hidden">
              <button
                onClick={() => toggleCategory(cat.id)}
                className="w-full flex items-center justify-between p-5 hover:bg-accent/50 transition-colors"
                data-testid={`category-${cat.id}`}
              >
                <div className="flex items-center gap-3">
                  <CategoryIcon iconName={cat.icon} className="h-5 w-5 text-[#3b82f6]" />
                  <span className="font-semibold text-base">{cat.name}</span>
                  <Badge variant="secondary" className="text-xs">
                    {cat.document_count} document{cat.document_count !== 1 ? 's' : ''}
                  </Badge>
                </div>
                {expandedCategories[cat.id]
                  ? <ChevronUp className="h-5 w-5 text-muted-foreground" />
                  : <ChevronDown className="h-5 w-5 text-muted-foreground" />
                }
              </button>

              {expandedCategories[cat.id] && (
                <div className="border-t px-5 pb-4" data-testid={`category-content-${cat.id}`}>
                  {!documents[cat.id] ? (
                    <div className="py-6 text-center text-muted-foreground">Loading...</div>
                  ) : documents[cat.id].length === 0 ? (
                    <div className="py-8 text-center">
                      <FolderOpen className="h-10 w-10 text-muted-foreground mx-auto mb-3 opacity-40" />
                      <p className="text-muted-foreground">No documents in this category</p>
                      {isAdmin && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="mt-3"
                          onClick={() => { setUploadCategoryId(cat.id); setUploadDialogOpen(true); }}
                        >
                          <Upload className="h-4 w-4 mr-2" />
                          Upload First Document
                        </Button>
                      )}
                    </div>
                  ) : (
                    <div className="space-y-1 pt-3">
                      {documents[cat.id].map(doc => (
                        <DocumentRow
                          key={doc.id}
                          doc={doc}
                          onDownload={handleDownload}
                          onDelete={isAdmin ? () => handleDelete(doc.id, cat.id) : null}
                          downloadingId={downloadingId}
                        />
                      ))}
                    </div>
                  )}
                </div>
              )}
            </Card>
          ))}

          {isAdmin && (
            <Button
              variant="outline"
              className="w-full border-dashed py-6"
              onClick={() => setCategoryDialogOpen(true)}
              data-testid="add-category-btn"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Category
            </Button>
          )}
        </div>
      )}

      {/* Upload Dialog */}
      <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Upload Document</DialogTitle>
            <DialogDescription>Add a document to the library for your team</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Category *</Label>
              <Select value={uploadCategoryId} onValueChange={setUploadCategoryId}>
                <SelectTrigger className="mt-1" data-testid="upload-category-select">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map(cat => (
                    <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Title *</Label>
              <Input
                value={uploadTitle}
                onChange={(e) => setUploadTitle(e.target.value)}
                placeholder="Document title"
                className="mt-1"
                data-testid="upload-title-input"
              />
            </div>
            <div>
              <Label>Description</Label>
              <Input
                value={uploadDescription}
                onChange={(e) => setUploadDescription(e.target.value)}
                placeholder="Brief description"
                className="mt-1"
              />
            </div>
            <div>
              <Label>File *</Label>
              <Input
                type="file"
                onChange={(e) => {
                  const f = e.target.files[0];
                  setUploadFile(f);
                  if (f && !uploadTitle) setUploadTitle(f.name.replace(/\.[^/.]+$/, ''));
                }}
                className="mt-1"
                data-testid="upload-file-input"
              />
            </div>
          </div>
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setUploadDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={handleUpload}
              disabled={!uploadFile || !uploadTitle || !uploadCategoryId || uploading}
              className="bg-[#3b82f6] hover:bg-[#2563eb]"
              data-testid="upload-submit-btn"
            >
              {uploading ? 'Uploading...' : 'Upload'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Category Dialog */}
      <Dialog open={categoryDialogOpen} onOpenChange={setCategoryDialogOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Add Category</DialogTitle>
            <DialogDescription>Create a new document category</DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label>Category Name *</Label>
            <Input
              value={newCategoryName}
              onChange={(e) => setNewCategoryName(e.target.value)}
              placeholder="e.g., Compliance Documents"
              className="mt-1"
              data-testid="category-name-input"
            />
          </div>
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setCategoryDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={handleCreateCategory}
              disabled={!newCategoryName.trim()}
              className="bg-[#3b82f6] hover:bg-[#2563eb]"
              data-testid="create-category-btn"
            >
              Create
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function DocumentRow({ doc, onDownload, onDelete, downloadingId }) {
  return (
    <div
      className="flex items-center justify-between p-3 rounded-lg hover:bg-accent/50 transition-colors group"
      data-testid={`document-row-${doc.id}`}
    >
      <div className="flex items-center gap-3 flex-1 min-w-0">
        <FileTypeIcon fileType={doc.file_type} />
        <div className="flex-1 min-w-0">
          <p className="font-medium text-sm truncate">{doc.title}</p>
          {doc.description && (
            <p className="text-xs text-muted-foreground truncate">{doc.description}</p>
          )}
          <div className="flex items-center gap-2 mt-1">
            <Badge variant="outline" className="text-[10px] px-1.5 py-0">{doc.file_type}</Badge>
            <span className="text-[10px] text-muted-foreground">{doc.file_size_label}</span>
          </div>
        </div>
      </div>
      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => onDownload(doc)}
          disabled={downloadingId === doc.id}
          data-testid={`download-btn-${doc.id}`}
        >
          <Download className="h-4 w-4" />
        </Button>
        {onDelete && (
          <Button
            variant="ghost"
            size="icon"
            onClick={() => onDelete(doc.id, doc.category_id)}
            className="text-destructive hover:text-destructive"
            data-testid={`delete-btn-${doc.id}`}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        )}
      </div>
    </div>
  );
}
